import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tag } from "lucide-react";
import { useExpertises } from "@/hooks/useDirectory";
import { PageHeader, SearchInput } from "@/components/common/Layout";
import { EmptyState, ErrorState, LoadingGrid } from "@/components/common/States";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { normalizeText } from "@/lib/text";
import { formatNumber } from "@/lib/format";

const CATEGORY_ORDER = ["IA & Data", "Cybersécurité", "Infrastructures & IT", "Innovation & Transformation",
  "Industries & Territoires", "Talents & Société", "Usages sectoriels", "Autres"];

const groupByCategory = (items) => CATEGORY_ORDER
  .map((c) => [c, items.filter((e) => (e.category || "Autres") === c)])
  .filter(([, rows]) => rows.length);

const SORTS = { name: (a, b) => a.name.localeCompare(b.name, "fr"), persons: (a, b) => b.persons_count - a.persons_count };

export default function ExpertisesPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("name");
  const { data, isLoading, isError, refetch } = useExpertises();
  const items = (data || []).filter((e) => normalizeText(e.name).includes(normalizeText(q))).sort(SORTS[sort]);
  const head = (key, label, cls = "") => (
    <TableHead className={cls}>
      <button type="button" onClick={() => setSort(key)} className={sort === key ? "font-bold text-foreground" : ""} data-testid={`expertises-sort-${key}`}>{label}</button>
    </TableHead>
  );
  return (
    <div className="lm-container pb-20">
      <PageHeader eyebrow="Écosystème" title="Expertises" icon={Tag} color="#0D9488"
        description="Les expertises renseignées sur les fiches Personnes et Entreprises." />
      <SearchInput className="mb-6 max-w-md" value={q} onChange={setQ} testId="expertises-search-input" placeholder="Rechercher une expertise…" />
      {isLoading ? <LoadingGrid /> : isError ? <ErrorState onRetry={refetch} /> : !items.length ? (
        <EmptyState description="Aucune expertise ne correspond à votre recherche." />
      ) : (
        <div className="lm-card overflow-hidden" data-testid="expertises-table">
          <Table>
            <TableHeader>
              <TableRow>{head("name", "Expertise")}{head("persons", "Personnes", "text-right")}<TableHead className="text-right">Entreprises</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {groupByCategory(items).map(([category, rows]) => (
                <Fragment key={category}>
                  <TableRow className="bg-stone-50/80 hover:bg-stone-50/80" data-testid={`expertise-category-${category}`}>
                    <TableCell colSpan={3} className="py-2.5">
                      <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style={{ color: rows[0].color }}>
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: rows[0].color }} />{category}
                        <span className="font-semibold normal-case tracking-normal text-stone-400">· {rows.length}</span>
                      </span>
                    </TableCell>
                  </TableRow>
                  {rows.map((e) => (
                    <TableRow key={e.id} className="cursor-pointer" onClick={() => navigate(`/expertises/${e.id}`)} data-testid={`expertise-row-${e.id}`}>
                      <TableCell className="pl-8 font-semibold">{e.name}</TableCell>
                      <TableCell className="text-right font-bold text-[#0D9488]">{formatNumber(e.persons_count)}</TableCell>
                      <TableCell className="text-right text-stone-600">{formatNumber(e.organizations_count)}</TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
